import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, MessageCircle, Video, Eye, EyeOff, Image as ImageIcon } from "lucide-react";
import { api, errMsg, eur, fmtDate, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, Empty, Field, PageHead, Pill } from "@/components/common";
import { FileUpload, Section } from "@/components/shared";

const EMPTY = { name: "", email: "", phone: "", password: "", pet_name: "", stream_url: "", stream_note: "", active: true };

const ClienteDialog = ({ open, onOpenChange, initial, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  useEffect(() => { if (open) setF({ ...EMPTY, ...(initial || {}), password: "" }); }, [open, initial]);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const save = async () => {
    try {
      const payload = { ...f, password: f.password || null, email: f.email.includes("@cliente.local") ? "" : f.email };
      const { data } = f.id ? await api.put(`/admin/clienti/${f.id}`, payload) : await api.post("/admin/clienti", payload);
      toast.success("Cliente salvato"); onSaved(data); onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#FAF8F5] max-w-2xl" data-testid="cliente-dialog">
        <DialogHeader><DialogTitle className="font-display text-3xl text-[#0D3B52]">{f.id ? "Modifica accesso cliente" : "Nuovo accesso cliente"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Nome cliente *"><Input value={f.name} onChange={set("name")} className="bg-white" data-testid="cliente-name" /></Field>
          <Field label="Nome animale"><Input value={f.pet_name} onChange={set("pet_name")} className="bg-white" data-testid="cliente-pet" /></Field>
          <Field label="Email (accesso)"><Input value={f.email?.includes("@cliente.local") ? "" : f.email} onChange={set("email")} className="bg-white" data-testid="cliente-email" /></Field>
          <Field label="Cellulare (accesso + WhatsApp)"><Input value={f.phone} onChange={set("phone")} className="bg-white" data-testid="cliente-phone" /></Field>
          <Field label={f.id ? "Nuova password" : "Password *"} hint="La comunichi tu al cliente" className="col-span-2"><Input value={f.password} onChange={set("password")} className="bg-white" data-testid="cliente-password" /></Field>
          <Field label="Link streaming cremazione teleassistita" className="col-span-2"><Input placeholder="https://… (YouTube Live, Meet, Zoom…)" value={f.stream_url} onChange={set("stream_url")} className="bg-white" data-testid="cliente-stream" /></Field>
          <Field label="Nota per il cliente (giorno/ora)" className="col-span-2"><Textarea rows={2} value={f.stream_note} onChange={set("stream_note")} className="bg-white" /></Field>
        </div>
        <div className="flex justify-end"><Button onClick={save} data-testid="cliente-save" className="rounded-full bg-[#0D3B52] hover:bg-[#082535] px-8">Salva</Button></div>
      </DialogContent>
    </Dialog>
  );
};

const ClienteFiles = ({ c, isAdmin }) => {
  const [files, setFiles] = useState([]);
  const load = useCallback(() => api.get(`/admin/clienti/${c.id}/files`).then((r) => setFiles(r.data)), [c.id]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="mt-3 text-sm space-y-1">
      {files.map((f) => <div key={f.id} className="flex justify-between items-center"><span><Video className="inline h-3.5 w-3.5 mr-1 text-[#B8860B]" />{f.title}</span>{isAdmin && <button onClick={async () => { await api.delete(`/admin/client-files/${f.id}`); load(); }}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></button>}</div>)}
      {isAdmin && <FileUpload url={`/admin/clienti/${c.id}/files`} fields={{ title: `Video ricordo ${c.pet_name || ""}`.trim() }} accept="video/*,application/pdf,image/*" label="Carica video / documento" onDone={load} testid={`cliente-upload-${c.id}`} />}
    </div>
  );
};

const ClientiTab = () => {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get("/admin/clienti").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);
  return (
    <>
      <div className="flex justify-end mb-4"><Button onClick={() => setEdit({})} data-testid="cliente-new" className="rounded-full bg-[#0D3B52] hover:bg-[#082535]"><Plus className="h-4 w-4 mr-1" />Nuovo accesso cliente</Button></div>
      {items.length === 0 ? <Empty>Nessun cliente con accesso all'app.</Empty> : (
        <div className="grid md:grid-cols-2 gap-4">
          {items.map((c) => (
            <Card key={c.id} className="p-5" data-testid={`cliente-row-${c.id}`}>
              <div className="flex justify-between items-start">
                <div><p className="font-semibold text-[#0D3B52]">{c.name} <span className="text-[#997316]">· {c.pet_name}</span></p><p className="text-xs text-[#5C6B73]">{c.email?.includes("@cliente.local") ? "" : c.email} {c.phone} · pw: {c.password_iniziale || "—"}</p>{c.stream_url && <Pill cls="bg-sky-50 text-[#0D3B52] border-sky-200 mt-1">Teleassistita attiva</Pill>}</div>
                <div className="flex gap-1">
                  <a href={c.whatsapp_welcome_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full bg-[#25D366] text-white px-3 h-9 text-xs font-medium" data-testid={`cliente-wa-${c.id}`}><MessageCircle className="h-4 w-4" />Ringraziamenti + app</a>
                  <button type="button" onClick={async () => { if (!window.confirm("Generare una nuova password e inviare le credenziali su WhatsApp?")) return; try { const { data } = await api.post(`/admin/users/${c.id}/reset-password`); toast.success(`Nuova password: ${data.password}`); if (data.whatsapp_url) window.open(data.whatsapp_url, "_blank"); load(); } catch (e) { toast.error(errMsg(e)); } }}
                    className="inline-flex items-center gap-1 rounded-full border border-[#25D366] text-[#1DA851] px-3 h-9 text-xs font-medium bg-white" data-testid={`cliente-cred-${c.id}`}><MessageCircle className="h-4 w-4" />Credenziali</button>
                  {isAdmin && <Button size="icon" variant="ghost" onClick={() => setEdit(c)} data-testid={`cliente-edit-${c.id}`}><Pencil className="h-4 w-4" /></Button>}
                  {isAdmin && <Button size="icon" variant="ghost" onClick={async () => { if (window.confirm("Eliminare l'accesso?")) { await api.delete(`/admin/clienti/${c.id}`); load(); } }}><Trash2 className="h-4 w-4 text-rose-600" /></Button>}
                </div>
              </div>
              <ClienteFiles c={c} isAdmin={isAdmin} />
            </Card>
          ))}
        </div>
      )}
      <ClienteDialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)} initial={edit} onSaved={load} />
    </>
  );
};

const CimiteroTab = () => {
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.get("/admin/cimitero").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);
  return items.length === 0 ? <Empty>Nessuna dedica ancora.</Empty> : (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {items.map((m) => (
        <Card key={m.id} className="overflow-hidden" data-testid={`memorial-admin-${m.id}`}>
          <img src={`${BACKEND_URL}/api/public/cimitero/${m.id}/foto`} alt={m.pet_name} className="h-44 w-full object-cover" />
          <div className="p-4">
            <p className="font-display text-xl font-semibold text-[#0D3B52]">{m.pet_name}</p>
            <p className="text-sm text-[#5C6B73] italic">"{m.dedica}"</p>
            <p className="text-xs text-[#5C6B73] mt-1">{m.owner_name} · {fmtDate(m.created_at)}</p>
            <div className="flex gap-2 mt-3">
              <Button size="sm" variant="outline" onClick={async () => { await api.patch(`/admin/cimitero/${m.id}?visibile=${!m.visibile}`); load(); }} data-testid={`memorial-toggle-${m.id}`}>{m.visibile ? <><EyeOff className="h-4 w-4 mr-1" />Nascondi</> : <><Eye className="h-4 w-4 mr-1" />Pubblica</>}</Button>
              <Button size="sm" variant="ghost" onClick={async () => { if (window.confirm("Eliminare?")) { await api.delete(`/cliente/cimitero/${m.id}`); load(); } }}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
};

const CatalogoTab = () => {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ nome: "", descrizione: "", prezzo: "", categoria: "Urne" });
  const [file, setFile] = useState(null);
  const load = useCallback(() => api.get("/admin/catalogo").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    if (!f.nome.trim()) return toast.error("Inserisci il nome");
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => fd.append(k, v));
    if (file) fd.append("file", file);
    try { await api.post("/admin/catalogo", fd); toast.success("Articolo aggiunto"); setF({ nome: "", descrizione: "", prezzo: "", categoria: f.categoria }); setFile(null); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <Section title="Nuovo articolo">
        <div className="space-y-3">
          <Field label="Nome *"><Input value={f.nome} onChange={(e) => setF({ ...f, nome: e.target.value })} className="bg-white" data-testid="cat-nome" /></Field>
          <Field label="Categoria"><Input value={f.categoria} onChange={(e) => setF({ ...f, categoria: e.target.value })} className="bg-white" data-testid="cat-categoria" /></Field>
          <Field label="Prezzo €"><Input value={f.prezzo} onChange={(e) => setF({ ...f, prezzo: e.target.value })} className="bg-white" data-testid="cat-prezzo" /></Field>
          <Field label="Descrizione"><Textarea rows={3} value={f.descrizione} onChange={(e) => setF({ ...f, descrizione: e.target.value })} className="bg-white" data-testid="cat-desc" /></Field>
          <Field label="Foto"><input type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm" data-testid="cat-foto" /></Field>
          <Button onClick={add} data-testid="cat-save" className="rounded-full bg-[#0D3B52] hover:bg-[#082535]">Aggiungi al catalogo</Button>
          <p className="text-xs text-[#5C6B73]">Il catalogo WhatsApp non è leggibile automaticamente: copia qui foto e descrizioni dei tuoi articoli. Sono visibili solo ai clienti che accedono dall'app.</p>
        </div>
      </Section>
      <div className="lg:col-span-2 grid sm:grid-cols-2 gap-4">
        {items.length === 0 ? <Empty>Catalogo vuoto.</Empty> : items.map((c) => (
          <Card key={c.id} className="overflow-hidden" data-testid={`cat-row-${c.id}`}>
            {c.storage_path ? <img src={`${BACKEND_URL}/api/catalogo/${c.id}/foto`} alt={c.nome} className="h-40 w-full object-cover" /> : <div className="h-40 bg-[#F3EFEA] flex items-center justify-center text-[#9AA5AB]"><ImageIcon className="h-8 w-8" /></div>}
            <div className="p-4">
              <div className="flex justify-between items-start"><div><p className="font-semibold text-[#0D3B52]">{c.nome}</p><p className="text-xs text-[#997316]">{c.categoria}</p></div><p className="font-display text-xl text-[#0D3B52]">{c.prezzo != null ? eur(c.prezzo) : ""}</p></div>
              <p className="text-sm text-[#5C6B73] mt-1">{c.descrizione}</p>
              <div className="flex gap-2 mt-3 items-center">
                <FileUpload url={`/admin/catalogo/${c.id}/foto`} accept="image/*" label="Foto" onDone={load} testid={`cat-photo-${c.id}`} />
                <Button size="sm" variant="outline" onClick={async () => { await api.put(`/admin/catalogo/${c.id}`, { ...c, visibile: !c.visibile }); load(); }}>{c.visibile ? "Nascondi" : "Mostra"}</Button>
                <Button size="icon" variant="ghost" onClick={async () => { if (window.confirm("Eliminare?")) { await api.delete(`/admin/catalogo/${c.id}`); load(); } }}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default function AreaClienti() {
  return (
    <div data-testid="admin-area-clienti">
      <PageHead eyebrow="App clienti" title="Clienti app, cimitero e catalogo" subtitle="Crea gli accessi per i clienti (email o cellulare + password), attiva la cremazione teleassistita, carica il video ricordo, modera il Cimitero virtuale e gestisci il catalogo." />
      <Tabs defaultValue="clienti">
        <TabsList className="bg-white border border-[#E2DDD5] mb-6">
          {[["clienti", "Accessi clienti"], ["cimitero", "Cimitero virtuale"], ["catalogo", "Catalogo"]].map(([v, l]) => <TabsTrigger key={v} value={v} data-testid={`clienti-tab-${v}`} className="data-[state=active]:bg-[#0D3B52] data-[state=active]:text-white">{l}</TabsTrigger>)}
        </TabsList>
        <TabsContent value="clienti"><ClientiTab /></TabsContent>
        <TabsContent value="cimitero"><CimiteroTab /></TabsContent>
        <TabsContent value="catalogo"><CatalogoTab /></TabsContent>
      </Tabs>
    </div>
  );
}
